import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

import { isString } from "lodash";
import { useEffect, useRef, useState } from "react";

import { CloudArrowUp, Funnel, ImageSquare, MagnifyingGlass, Sparkle, Upload, VideoCamera } from "@phosphor-icons/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useDebounce, useIntersectionObserver } from "@uidotdev/usehooks";

import { LibraryApiFactory } from "@/apis/library";
import { Library } from "@/schema/library";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { LibraryCard } from "@/components/card/library";
import { PageHeader } from "@/components/layout/heading";
import { LibraryAssetUploadModal } from "@/components/modals/asset-upload";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResponsiveMascot } from "@/components/animations/mascot";

export const Route = createFileRoute("/dashboard/ai-designer/_layout/library")({
  component: RouteComponent,
});

const mPageSize = 20;
const mGutterBreakpoints = { 320: 16 };
const mColumnBreakpoints = { 320: 1, 576: 2, 896: 3, 1280: 4, 1660: 5 };
const mContentStyle = { minHeight: "calc(100dvh - 18rem)" } as React.CSSProperties;

function RouteComponent() {
  const [search, setSearch] = useState("");
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);

  const [filters, setFilters] = useState({
    type: "",
    orientation: "",
    source: "system.generated",
  });

  const query = useDebounce(search, 500);
  const container$ = useRef<HTMLElement>(null!);

  const {
    data = [],
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: LibraryApiFactory.Keys.FetchInfiniteLibraryAssets({ search: query, media_type: filters.type, media_source: filters.source }),
    queryFn: ({ pageParam }) => {
      return LibraryApiFactory.Apis.FetchLibraryAssets({
        page: pageParam,
        size: mPageSize,
        search: query || undefined,
        media_type: filters.type || undefined,
        media_source: filters.source || undefined,
      });
    },
    getNextPageParam: (page) => {
      return page.page < page.pages ? page.page + 1 : null;
    },
    select: (data) => {
      return data.pages.flatMap((page) => page.items);
    },
  });

  const handleFilterChange = (key: string) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      [key]: isString(value) ? value : value.target.value,
    });
  };

  const handleRadioFilterChange = (_key: string) => (value: string) => {
    const key = _key as keyof typeof filters;
    setFilters({
      ...filters,
      [key]: filters[key] === value ? "" : value,
    });
  };

  const handleClearFilters = () => {
    setSearch("");
    setFilters({ type: "", orientation: "", source: "system.generated" });
  };

  const handleUploadSuccess = () => {
    setFilters((state) => ({ ...state, source: "user.uploaded" }));
  };

  return (
    <section id="library" ref={container$}>
      <PageHeader title="Media Library" description="Manage your media assets generated by our agents">
        <div className="flex items-center gap-4">
          <Tabs defaultValue="generated" value={filters.source} onValueChange={handleFilterChange("source")} className="hidden xl:flex">
            <TabsList className="w-64">
              <TabsTrigger value="system.generated">Generated</TabsTrigger>
              <TabsTrigger value="user.uploaded">Uploaded</TabsTrigger>
            </TabsList>
          </Tabs>
          <LibraryAssetUploadModal open={isUploadModalOpen} onOpenChange={setUploadModalOpen} onSuccess={handleUploadSuccess}>
            <Button className="gap-3 hidden lg:flex">
              <span>Upload New Asset</span>
              <CloudArrowUp weight="fill" className="size-4" />
            </Button>
          </LibraryAssetUploadModal>
        </div>
      </PageHeader>
      <div className="lg:mt-8 flex flex-col gap-8">
        <div className="flex flex-col lg:flex-row items-center gap-y-3 gap-x-6">
          <div className="relative w-full lg:flex-1 lg:w-auto">
            <MagnifyingGlass weight="bold" className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 size-4" />
            <Input placeholder="Search library..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="shrink-0 flex items-center gap-x-2 gap-y-3 w-full lg:w-auto flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex-1 lg:flex-initial">
                  <Funnel className="size-4" />
                  <span>Filters</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-44">
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="px-3.5">Media Type</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={filters.type} onValueChange={handleRadioFilterChange("type")}>
                    <DropdownMenuRadioItem value="image">
                      <span>Image</span>
                      <ImageSquare className="size-4 ml-auto" />
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="video">
                      <span>Video</span>
                      <VideoCamera className="size-4 ml-auto" />
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuGroup>
                <DropdownMenuGroup className="block xl:hidden">
                  <DropdownMenuLabel className="px-3.5">Media Source</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={filters.source} onValueChange={handleRadioFilterChange("source")}>
                    <DropdownMenuRadioItem value="system.generated">
                      <span>Generated</span>
                      <Sparkle className="size-4 ml-auto" />
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="user.uploaded">
                      <span>Uploaded</span>
                      <Upload className="size-4 ml-auto" />
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={handleClearFilters} className="flex-1 lg:flex-initial">
              <span>Clear Filters</span>
            </Button>
            <LibraryAssetUploadModal open={isUploadModalOpen} onOpenChange={setUploadModalOpen} onSuccess={handleUploadSuccess}>
              <Button className="gap-3 flex lg:hidden flex-1">
                <span>Upload Asset</span>
                <CloudArrowUp weight="fill" className="size-4" />
              </Button>
            </LibraryAssetUploadModal>
          </div>
        </div>
        {!data.length ? (
          !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full" style={mContentStyle}>
              <ResponsiveMascot mascot="MascotError" className="size-64 md:size-80 relative mx-auto -top-2 -right-2.5 md:right-3.5" />
              <p className="text-base font-medium -mt-10 mb-1">No media assets found</p>
              {filters.source === "system.generated" ? (
                <p className="text-sm text-muted-foreground">Media generated by our agents will appear here</p>
              ) : (
                <p className="text-sm text-muted-foreground">Media uploaded by you will appear here</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-96 w-full" />
              ))}
            </div>
          )
        ) : (
          <LibraryMasonryGrid data={data} canLoadMore={hasNextPage} onLoadMore={fetchNextPage} />
        )}
      </div>
    </section>
  );
}

interface LibraryMasonryGridProps {
  data: Library[];
  canLoadMore: boolean;
  onLoadMore: () => void;
}

function LibraryMasonryGrid({ data, canLoadMore, onLoadMore }: LibraryMasonryGridProps) {
  const [ref, entry] = useIntersectionObserver({
    root: null,
    threshold: 0,
    rootMargin: "50% 0px 0px 0px",
  });

  const visible = !!entry?.isIntersecting;

  useEffect(() => {
    if (!visible || !canLoadMore) return;
    onLoadMore();
  }, [visible, canLoadMore, onLoadMore]);

  return (
    <div className="relative">
      <ResponsiveMasonry columnsCountBreakPoints={mColumnBreakpoints} gutterBreakPoints={mGutterBreakpoints}>
        <Masonry>
          {data.map((item) => {
            return <LibraryCard key={item.id} item={item} />;
          })}
        </Masonry>
      </ResponsiveMasonry>
      <div ref={ref} className="flex items-center justify-center gap-2.5 pt-12 pb-4">
        {canLoadMore ? <div className="spinner size-5 !border-primary" /> : null}
        <span className="text-sm font-medium text-muted-foreground">{canLoadMore ? "Loading more assets..." : "End of results"}</span>
      </div>
    </div>
  );
}
